import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface AuthState {
  userToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  lastActivity: number
  isInitialized: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  resetActivityTimer: () => void
  checkSession: () => boolean
  initialize: () => Promise<void>
}

const INACTIVITY_TIMEOUT = 5 * 60 * 1000 // 5 minutes

let inactivityTimer: NodeJS.Timeout | null = null

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      userToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      lastActivity: Date.now(),
      isInitialized: false,

      initialize: async () => {
        const { userToken, lastActivity } = get()
        
        if (!userToken) {
          set({ isInitialized: true, isAuthenticated: false })
          return
        }

        // Check if session is still valid
        const timeSinceLastActivity = Date.now() - lastActivity
        if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
          console.log('[Auth] Session expired during initialization')
          await get().logout()
          set({ isInitialized: true })
          return
        }

        // Validate token with backend
        try {
          const response = await fetch(
            'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/validateToken',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${userToken}`,
              },
            }
          )

          if (!response.ok) {
            console.log('[Auth] Token validation failed')
            await get().logout()
            set({ isInitialized: true })
            return
          }

          // Token is valid
          set({ 
            isAuthenticated: true, 
            isInitialized: true,
            lastActivity: Date.now()
          })
          get().resetActivityTimer()
        } catch (error) {
          console.log('[Auth] Token validation error:', error)
          await get().logout()
          set({ isInitialized: true })
        }
      },
      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null })
        
        try {
          console.log('[Auth] login() – sending credentials to API...')
          
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 10000)

          const response = await fetch(
            'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/login',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
              body: JSON.stringify({ username, password }),
              signal: controller.signal,
            }
          )

          clearTimeout(timeoutId)
          console.log('[Auth] Response status:', response.status)

          const text = await response.text()
          console.log('[Auth] Raw response text:', text)
          
          let data: { token?: string; error?: string; message?: string } = {}
          
          try {
            data = JSON.parse(text)
            console.log('[Auth] Parsed JSON:', data)
          } catch (parseError) {
            console.warn('[Auth] Could not parse JSON from response:', parseError)
            throw new Error(`Server returned invalid JSON: ${text.substring(0, 100)}...`)
          }

          if (!response.ok) {
            const msg = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`
            throw new Error(msg)
          }

          if (!data.token) {
            throw new Error('Login succeeded but no token returned.')
          }

          console.log('[Auth] Login successful, saving token...')
          
          const now = Date.now()
          set({
            userToken: data.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            lastActivity: now,
            isInitialized: true,
          })

          // Clear any existing timer
          if (inactivityTimer) {
            clearTimeout(inactivityTimer)
          }

          // Start activity tracking
          get().resetActivityTimer()
          
        } catch (error: any) {
          console.error('[Auth] Login error:', error)
          
          let errorMessage = 'Login failed'
          
          if (error.name === 'AbortError') {
            errorMessage = 'Request timed out. Please check your internet connection and try again.'
          } else if (error.message === 'Failed to fetch') {
            errorMessage = 'Unable to connect to server. Please check your internet connection and try again.'
          } else if (error.message?.includes('NetworkError')) {
            errorMessage = 'Network error. Please check your internet connection.'
          } else if (error.message) {
            errorMessage = error.message
          }
          
          set({
            error: errorMessage,
            isLoading: false,
            isInitialized: true,
          })
          throw error
        }
      },

      logout: async () => {
        console.log('[Auth] Logging out...')
        
        // Clear timers
        if (inactivityTimer) {
          clearTimeout(inactivityTimer)
          inactivityTimer = null
        }

        // Mark that user was authenticated before logout (for session expiry detection)
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('wasAuthenticated', 'true')
        }
        set({ 
          userToken: null, 
          isAuthenticated: false, 
          error: null, 
          lastActivity: Date.now(),
          isInitialized: true,
        })
      },

      resetActivityTimer: () => {
        const { userToken } = get()
        if (!userToken) return

        // Clear existing timer
        if (inactivityTimer) {
          clearTimeout(inactivityTimer)
        }

        // Update last activity
        const now = Date.now()
        set({ lastActivity: now })

        // Set new timer - but make it less aggressive
        inactivityTimer = setTimeout(() => {
          console.log('[Auth] Auto-logout due to inactivity')
          get().logout()
          // Force redirect to login
          if (typeof window !== 'undefined') {
            window.location.href = '/login'
          }
        }, INACTIVITY_TIMEOUT)
      },

      checkSession: () => {
        const { userToken, lastActivity } = get()
        if (!userToken) return false
        
        const timeSinceLastActivity = Date.now() - lastActivity
        if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
          console.log('[Auth] Session expired due to inactivity')
          get().logout()
          // Force redirect to login
          if (typeof window !== 'undefined') {
            window.location.href = '/login'
          }
          return false
        }
        
        return true
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        userToken: state.userToken,
        lastActivity: state.lastActivity,
      }),
    }
  )
)

// Simplified activity tracking for web
if (typeof window !== 'undefined') {
  // Activity events
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

  let lastActivityTime = Date.now()
  const throttleDelay = 5000 // Only reset timer once per 5 seconds

  const handleActivity = () => {
    const now = Date.now()
    if (now - lastActivityTime > throttleDelay) {
      lastActivityTime = now
      const { isAuthenticated, resetActivityTimer } = useAuthStore.getState()
      if (isAuthenticated) {
        resetActivityTimer()
      }
    }
  }

  activityEvents.forEach(event => {
    document.addEventListener(event, handleActivity, { passive: true })
  })
}